/**
 * Selector de categorías jerárquico — Sprint 17 S17-Z6.
 *
 * Renderiza un `<select>` nativo con `<optgroup>` para las categorías
 * raíz que tienen hijas. Las raíces terminales (sin hijas) son
 * seleccionables directamente. Las raíces con hijas NO son seleccionables
 * (el `<optgroup label="...">` no es opción).
 *
 * Doctrina cerrada 2026-05-26: obligar a elegir hoja siempre que el
 * padre tenga hijas, para evitar imputación ambigua ("Mantención" sin
 * saber si es plaza o portones).
 *
 * Reemplaza el `<select>` plano que listaba padres + hijas mezclados
 * (Gastos, Compensaciones, IngresosVarios).
 */
import type { CategoriaRead } from '../../lib/api/types';

export interface CategoriaSelectorProps {
  categorias: CategoriaRead[];
  value: string;
  onChange: (id: string) => void;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function CategoriaSelector({
  categorias,
  value,
  onChange,
  id,
  required,
  disabled,
  placeholder = '— elige —',
  className,
}: CategoriaSelectorProps) {
  // Construir árbol: raíces (sin padre) y hijas agrupadas por padre.
  const raices: CategoriaRead[] = [];
  const hijasPorPadre = new Map<string, CategoriaRead[]>();
  for (const c of categorias) {
    if (c.categoria_padre_id === null) {
      raices.push(c);
    } else {
      const list = hijasPorPadre.get(c.categoria_padre_id) ?? [];
      list.push(c);
      hijasPorPadre.set(c.categoria_padre_id, list);
    }
  }
  const raicesOrdenadas = [...raices].sort((a, b) =>
    a.nombre.localeCompare(b.nombre),
  );

  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      disabled={disabled}
      className={className}
    >
      <option value="">{placeholder}</option>
      {raicesOrdenadas.map((raiz) => {
        const hijas = hijasPorPadre.get(raiz.id) ?? [];
        if (hijas.length === 0) {
          // Raíz terminal — opción seleccionable directamente.
          return (
            <option key={raiz.id} value={raiz.id}>
              {raiz.nombre}
            </option>
          );
        }
        // Raíz con hijas — agrupador, no seleccionable.
        const hijasOrdenadas = [...hijas].sort((a, b) =>
          a.nombre.localeCompare(b.nombre),
        );
        return (
          <optgroup key={raiz.id} label={raiz.nombre}>
            {hijasOrdenadas.map((h) => (
              <option key={h.id} value={h.id}>
                {h.nombre}
              </option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );
}
